import { CompanySubscription } from 'common/types/responses'

/**
 * Determines if a subscription is an addon-only subscription that should be hidden
 * when other main subscriptions exist in the company.
 *
 * Currently identifies addon subscriptions by name (e.g., "WhatsApp for Business Number").
 * These should only be shown if they're the only subscription in the company.
 *
 * @param subscription - The subscription to check
 * @returns true if this is an addon-only subscription
 */
export const isAddonOnlySubscription = (
  subscription: CompanySubscription,
): boolean => {
  const addonNames = ['WhatsApp for Business Number']
  return addonNames.some((name) =>
    subscription.name.toLowerCase().includes(name.toLowerCase()),
  )
}

/**
 * Filters company subscriptions to hide addon-only subscriptions when
 * other main subscriptions exist.
 *
 * @param subscriptions - Array of company subscriptions
 * @returns Filtered array of subscriptions
 */
export const filterVisibleSubscriptions = (
  subscriptions: CompanySubscription[],
): CompanySubscription[] => {
  if (!subscriptions || subscriptions.length === 0) {
    return []
  }

  // Check if company has any main (non-addon) subscriptions
  const hasMainSubscriptions = subscriptions.some(
    (sub) => !isAddonOnlySubscription(sub),
  )

  // If there are main subscriptions, filter out addon-only ones
  if (hasMainSubscriptions) {
    return subscriptions.filter((sub) => !isAddonOnlySubscription(sub))
  }

  // If only addon subscriptions exist, show them all
  return subscriptions
}
